import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";
import { useFamilyStore } from "../stores/familyStore";
import { useAuthStore } from "../stores/authStore";
import type { Kid, Challenge, Room, TaskAssignment, Transaction } from "@starquest/core";
import { xpProgressInLevel } from "@starquest/core";
import styles from "./KidHome.module.css";
import PreviewBanner, { isPreviewMode } from "../components/PreviewBanner";
import ThemeWrapper from "../components/ThemeWrapper";
import { useTheme } from "../hooks/useTheme";

export default function KidHome() {
  const navigate = useNavigate();
  const { familyId: authFamilyId, clearAuth } = useAuthStore();
  // Kid selector shown if family is loaded but no kid picked
  const { kids, rooms, challenges, selectedKid, setKids, setRooms, setChallenges, selectKid } = useFamilyStore();

  // Detect PIN-authenticated kid session — sq_kid_id is only set via KidLogin PIN flow
  const authenticatedKidId = localStorage.getItem("sq_kid_id") ?? null;
  const isKidPinSession = !!authenticatedKidId;

  // On restart: authFamilyId and selectedKid are both null for PIN sessions.
  // Read familyId from sq_last_family so data can load and auto-select the kid.
  const storedFamilyId = (() => {
    if (!authenticatedKidId) return null;
    try { return JSON.parse(localStorage.getItem("sq_last_family") ?? "null")?.familyId ?? null; }
    catch { return null; }
  })();
  const familyId = authFamilyId || selectedKid?.familyId || storedFamilyId || null;

  const theme = useTheme(selectedKid);
  const [loading,      setLoading]      = useState(true);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [catchStar,    setCatchStar]    = useState<Challenge | null>(null);
  const [uploading,    setUploading]    = useState(false);
  const [submitting,   setSubmitting]   = useState(false);
  const [submitted,    setSubmitted]    = useState<Challenge | null>(null);
  const photoRef = useRef<HTMLInputElement>(null);

  const [taskAssignments, setLocalTaskAssignments] = useState<TaskAssignment[]>([]);
  const [transactions,    setTransactions]          = useState<Transaction[]>([]);
  const [submittingTask,  setSubmittingTask]         = useState<string | null>(null);
  const [showNotifs,      setShowNotifs]             = useState(false);
  const [editingNick,     setEditingNick]            = useState(false);
  const [nickDraft,       setNickDraft]              = useState("");
  const [pickingAvatar,   setPickingAvatar]          = useState(false);
  const [changingPin,     setChangingPin]            = useState(false);
  const [pinStep,         setPinStep]                = useState<"new" | "confirm">("new");
  const [newPin,          setNewPin]                 = useState("");
  const [confirmPin,      setConfirmPin]             = useState("");
  const [pinError,        setPinError]               = useState("");
  const [pinSaving,       setPinSaving]              = useState(false);

  const [dayOffset,         setDayOffset]         = useState(0);
  const [taskPage,          setTaskPage]          = useState(0);
  const [readNotifIds,      setReadNotifIds]      = useState<Set<string>>(() => {
    const k = selectedKid?.kidId;
    if (!k) return new Set();
    try { return new Set(JSON.parse(localStorage.getItem(`sq_nr_${k}`) ?? "[]")); } catch { return new Set(); }
  });
  const [dismissedNotifIds, setDismissedNotifIds] = useState<Set<string>>(() => {
    const k = selectedKid?.kidId;
    if (!k) return new Set();
    try { return new Set(JSON.parse(localStorage.getItem(`sq_nd_${k}`) ?? "[]")); } catch { return new Set(); }
  });
  // null = auto (open if quests exist), true/false = user-chosen
  const [houseOpenOverride, setHouseOpenOverride] = useState<boolean | null>(null);
  const [questRequested,    setQuestRequested]    = useState(false);

  function handleAskForQuest() {
    setQuestRequested(true);
    // TODO: wire to API to notify parent
  }

  useEffect(() => {
    if (!familyId) return;
    setLoading(true);
    Promise.all([
      api.listKids(familyId).then((k) => setKids(k as Kid[])),
      api.listRooms(familyId).then((r) => setRooms(r as Room[])),
      api.listChallenges(familyId).then((c) => setChallenges(c as Challenge[])),
    ]).finally(() => setLoading(false));
  }, [familyId]);

  useEffect(() => {
    const k = selectedKid;
    if (!familyId || !k) return;
    api.listTaskAssignments(familyId, { kidId: k.kidId }).then((a) => setLocalTaskAssignments(a as TaskAssignment[]));
    api.listTransactions(familyId, k.kidId).then((t) => setTransactions(t as Transaction[]));
  }, [familyId, selectedKid?.kidId]);

  // Security: PIN-session kids cannot switch to other kids — auto-reselect if they somehow land on picker
  useEffect(() => {
    if (!isKidPinSession || selectedKid) return;
    const authed = kids.find((k) => k.kidId === authenticatedKidId);
    if (authed) selectKid(authed);
  }, [isKidPinSession, selectedKid, kids, authenticatedKidId]);

  // Reload notification state when the selected kid changes (different kid = different dismissed set)
  useEffect(() => {
    const k = selectedKid?.kidId;
    if (!k) return;
    try {
      setReadNotifIds(new Set(JSON.parse(localStorage.getItem(`sq_nr_${k}`) ?? "[]")));
      setDismissedNotifIds(new Set(JSON.parse(localStorage.getItem(`sq_nd_${k}`) ?? "[]")));
    } catch { /* ignore parse errors */ }
  }, [selectedKid?.kidId]);

  // Persist notification read/dismissed state to localStorage per kid
  useEffect(() => {
    const k = selectedKid?.kidId;
    if (!k) return;
    localStorage.setItem(`sq_nr_${k}`, JSON.stringify([...readNotifIds]));
  }, [readNotifIds, selectedKid?.kidId]);

  useEffect(() => {
    const k = selectedKid?.kidId;
    if (!k) return;
    localStorage.setItem(`sq_nd_${k}`, JSON.stringify([...dismissedNotifIds]));
  }, [dismissedNotifIds, selectedKid?.kidId]);

  function starsThisWeek(): number {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const monday = new Date(now);
    monday.setDate(monday.getDate() - daysFromMonday);
    monday.setHours(0, 0, 0, 0);
    return (transactions as Transaction[])
      .filter((t) => t.type === "earn" && new Date(t.createdAt) >= monday)
      .reduce((sum, t) => sum + t.amount, 0);
  }

  function todayStr(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }

  function mondayStr(): string {
    const now = new Date();
    const day = now.getDay();
    const daysBack = day === 0 ? 6 : day - 1;
    const monday = new Date(now);
    monday.setDate(monday.getDate() - daysBack);
    return `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, "0")}-${String(monday.getDate()).padStart(2, "0")}`;
  }

  function tierHint(dueDate: string): { text: string; cls: string } {
    const diff = (new Date(dueDate).getTime() - Date.now()) / (1000 * 60 * 60);
    if (diff >= 24) return { text: "🌟 Early bird! +20% bonus", cls: styles.taskHintEarly };
    if (diff > 0)   return { text: `⏰ Due in ${Math.max(1, Math.ceil(diff))}h`, cls: "" };
    return             { text: "⏱️ Overdue — 50% stars", cls: styles.taskHintLate };
  }

  function taskMatchesDate(a: import("@starquest/core").TaskAssignment, dateStr: string): boolean {
    if (a.status === "rejected") return false;
    const origDate = a.dueDate.slice(0, 10);

    // Already actioned (submitted/approved) or non-recurring: exact date match only
    if (!a.recurrence || a.status !== "assigned") return origDate === dateStr;

    // Daily recurring, still pending: show on the due date and every day after
    if (a.recurrence.type === "daily") return dateStr >= origDate;

    // Weekly recurring, still pending: show on matching days of week >= original due date
    if (a.recurrence.type === "weekly") {
      if (dateStr < origDate) return false;
      const d = new Date(dateStr + "T12:00:00");
      return (a.recurrence.days ?? []).includes(d.getDay());
    }

    return origDate === dateStr;
  }

  function buildNotifications() {
    const weekMs = 7 * 24 * 60 * 60 * 1000;
    const isRecent = (iso: string | null | undefined) =>
      iso ? Date.now() - new Date(iso).getTime() < weekMs : false;

    type Notif = { id: string; emoji: string; text: string; detail: string; at: string };
    const notifs: Notif[] = [];

    for (const a of taskAssignments) {
      if (a.status === "assigned" && !a.rejectionReason && isRecent(a.createdAt)) {
        notifs.push({ id: a.assignmentId, emoji: a.taskEmoji,
          text: `New task: ${a.taskName}`,
          detail: `Due ${new Date(a.dueDate).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}`,
          at: a.createdAt });
      }
      if (a.status === "approved" && isRecent(a.reviewedAt)) {
        notifs.push({ id: `ok-${a.assignmentId}`, emoji: "✅",
          text: `${a.taskName} approved!`,
          detail: `+${a.pointsAwarded ?? 0} ${theme.pointEmoji} earned`,
          at: a.reviewedAt! });
      }
    }

    for (const t of transactions) {
      if (t.type === "bonus" && isRecent(t.createdAt)) {
        notifs.push({ id: t.txnId, emoji: "🎁",
          text: "Bonus stars!",
          detail: `+${t.amount} ${theme.pointEmoji} · ${t.reason}`,
          at: t.createdAt });
      }
    }

    for (const c of activeChallenges) {
      if (isRecent(c.createdAt)) {
        notifs.push({ id: c.challengeId, emoji: c.emoji,
          text: `New challenge: ${c.challengeName}`,
          detail: `Find it in your house — ${c.pointReward} ${theme.pointEmoji} reward`,
          at: c.createdAt });
      }
    }

    return notifs.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  }

  function selectedDateStr(): string {
    const d = new Date();
    d.setDate(d.getDate() + dayOffset);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }

  function dayLabel(): string {
    if (dayOffset === 0)  return "Today";
    if (dayOffset === -1) return "Yesterday";
    if (dayOffset === 1)  return "Tomorrow";
    const d = new Date();
    d.setDate(d.getDate() + dayOffset);
    return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  }

  function changeDayOffset(delta: number) {
    setDayOffset((o) => o + delta);
    setTaskPage(0);
  }

  function markRead(id: string) {
    setReadNotifIds((prev) => new Set([...prev, id]));
  }

  function dismissNotif(id: string) {
    setDismissedNotifIds((prev) => new Set([...prev, id]));
    setReadNotifIds((prev) => new Set([...prev, id]));
  }

  const THEME_AVATARS: Record<string, string[]> = {
    base:     ["🧑‍🚀","🦸","🧙","🦄","🐉","🦊","🐼","🦁","🐯","🦋","⭐","🌟"],
    explorer: ["🗺️","🏕️","🧭","🏔️","🌋","🦅","🐆","🦁","🌿","⛺","🔭","🦊"],
    wizard:   ["🧙","🔮","✨","🪄","🦉","🐉","🌙","⚡","🌠","💫","🧝","🦄"],
    sports:   ["🏆","⚽","🏀","🎽","🥇","🏋️","🤸","🏊","🎯","💪","🦅","🏅"],
  };

  async function saveNickname() {
    if (!familyId || !selectedKid) return;
    const trimmed = nickDraft.trim();
    await api.updateKid(familyId, selectedKid.kidId, { nickname: trimmed || null });
    api.listKids(familyId).then((k) => setKids(k as Kid[]));
    setEditingNick(false);
  }

  async function saveAvatar(emoji: string) {
    if (!familyId || !selectedKid) return;
    await api.updateKid(familyId, selectedKid.kidId, { avatar: emoji });
    api.listKids(familyId).then((k) => setKids(k as Kid[]));
    setPickingAvatar(false);
  }

  function openChangPin() {
    setChangingPin(true); setPinStep("new");
    setNewPin(""); setConfirmPin(""); setPinError("");
  }

  async function submitPinChange() {
    if (!familyId || !selectedKid) return;
    if (newPin.length < 5) { setPinError("PIN must be at least 5 digits"); return; }
    if (pinStep === "new") {
      setPinStep("confirm"); setConfirmPin(""); setPinError(""); return;
    }
    if (newPin !== confirmPin) { setPinError("PINs don't match — try again"); setConfirmPin(""); return; }
    setPinSaving(true); setPinError("");
    try {
      await api.updateKid(familyId, selectedKid.kidId, { pin: newPin } as any);
      setChangingPin(false);
    } catch { setPinError("Failed to save. Try again."); }
    finally { setPinSaving(false); }
  }

  async function submitTask(assignmentId: string) {
    if (!familyId) return;
    setSubmittingTask(assignmentId);
    try {
      const updated = await api.submitTaskAssignment(familyId, assignmentId);
      setLocalTaskAssignments((prev) =>
        prev.map((a) => a.assignmentId === assignmentId ? (updated as TaskAssignment) : a)
      );
    } catch { alert("Failed to submit. Try again."); }
    finally  { setSubmittingTask(null); }
  }

  async function submitQuest(challenge: Challenge, photoFile?: File) {
    if (!familyId || !selectedKid) return;
    setSubmitting(true);
    try {
      let photoKey: string | null = null;

      if (photoFile) {
        setUploading(true);
        const { uploadUrl, photoKey: key } = await api.getUploadUrl(familyId, selectedKid.kidId, photoFile.type);
        await fetch(uploadUrl, { method: "PUT", body: photoFile, headers: { "Content-Type": photoFile.type } });
        photoKey = key;
        setUploading(false);
      }

      await api.createCompletion(familyId, { kidId: selectedKid.kidId, challengeId: challenge.challengeId, photoKey });
      setSubmitted(challenge);
      setCatchStar(null);
      setSelectedRoom(null);
      api.listChallenges(familyId).then((c) => setChallenges(c as Challenge[]));
    } catch (e) {
      alert("Failed to submit. Try again.");
    } finally { setSubmitting(false); setUploading(false); }
  }

  // Group active challenges by room
  const activeChallenges = challenges.filter((c) => c.isActive);
  const challengesByRoom = rooms.map((r) => ({
    ...r,
    roomChallenges: activeChallenges.filter((c) => c.roomId === r.roomId),
  })).filter((r) => r.isVisible);

  // Accordion: auto-open when quests exist, respect user toggle
  const isHouseOpen = houseOpenOverride !== null ? houseOpenOverride : activeChallenges.length > 0;
  function toggleHouse() { setHouseOpenOverride(!isHouseOpen); }

  const kid = selectedKid;
  const xpProg = kid ? xpProgressInLevel(kid.xp) : null;

  // Loading screen (before kid data arrives)
  if (loading && !kid) return (
    <div className={styles.pickerPage}>
      <div className={styles.pickerStars} aria-hidden />
      <div style={{ color:"rgba(240,240,255,0.4)", textAlign:"center", padding:40, fontFamily:"var(--font-body)" }}>
        Loading {(theme.kidRole + "s").toLowerCase()}…
      </div>
    </div>
  );

  // Kid picker screen — locked for PIN sessions (they see a sign-out only)
  if (!kid) {
    if (isKidPinSession) {
      // PIN session: should never reach here (useEffect auto-reselects), but show safe fallback
      return (
        <div className={styles.pickerPage}>
          <div className={styles.pickerStars} aria-hidden />
          <div style={{ color:"rgba(240,240,255,0.4)", textAlign:"center", padding:40, fontFamily:"var(--font-body)" }}>
            Loading your profile…
          </div>
          <button className={styles.backBtn} onClick={() => { clearAuth(); navigate("/"); }}>
            Sign Out
          </button>
        </div>
      );
    }
    return (
      <div className={styles.pickerPage}>
        <div className={styles.pickerStars} aria-hidden />
        <h2 className={styles.pickerTitle}>Who's playing today?</h2>
        <div className={styles.pickerGrid}>
          {kids.map((k) => (
            <button key={k.kidId} className={styles.kidCard} onClick={() => selectKid(k)}>
              <div className={styles.kidCardAvi}>{k.avatar}</div>
              <div className={styles.kidCardName}>{k.name}</div>
              <div className={styles.kidCardBal}>{theme.pointEmoji} {k.pointBalance}</div>
            </button>
          ))}
        </div>
        <button className={styles.backBtn} onClick={() => navigate("/parent")}>← Back</button>
      </div>
    );
  }

  return (
    <ThemeWrapper kid={selectedKid}>
    <div className={styles.page} style={isPreviewMode() ? { paddingTop: 52 } : undefined}>
      <PreviewBanner />
      <div className={styles.starbg} aria-hidden />

      {/* HUD */}
      <div className={styles.hud}>
        {/* Back button only shown in parent-preview mode — PIN kids use footer Sign Out */}
        {!isKidPinSession && (
          <button className={styles.hudBack} onClick={() => selectKid(null)}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18 L9 12 L15 6"/></svg>
          </button>
        )}
        <div className={styles.kidCard2}>
          {/* Avatar — tap to pick emoji */}
          <button onClick={() => setPickingAvatar((v) => !v)} className={styles.hudAvi} title="Change avatar"
            style={{ cursor: "pointer", border: "2px solid rgba(255,215,0,0.5)", flexShrink: 0 }}>
            {kid.avatar}
          </button>

          <div className={styles.hudInfo}>
            {editingNick ? (
              /* Editing: replace the greeting with the input field */
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <input
                  style={{ fontSize: 15, fontWeight: 900, fontFamily: "var(--font-display)", padding: "3px 8px", borderRadius: 8, border: "1px solid rgba(255,215,0,0.5)", background: "rgba(0,0,0,0.4)", color: "#fff", width: 130 }}
                  placeholder="Your nickname…"
                  value={nickDraft}
                  onChange={(e) => setNickDraft(e.target.value)}
                  autoFocus maxLength={24}
                  onKeyDown={(e) => { if (e.key === "Enter") saveNickname(); if (e.key === "Escape") setEditingNick(false); }}
                />
                <button onClick={saveNickname} style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, background: "var(--gold)", color: "#1a1a2e", fontWeight: 900, border: "none", cursor: "pointer" }}>✓</button>
                <button onClick={() => setEditingNick(false)} style={{ fontSize: 11, padding: "3px 6px", borderRadius: 6, background: "none", color: "rgba(240,240,255,0.5)", border: "none", cursor: "pointer" }}>✕</button>
              </div>
            ) : (
              /* Normal: greeting + nickname label with edit pencil */
              <div className={styles.hudName} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                Hi {kid.nickname || kid.name}!
                <button onClick={() => { setNickDraft(kid.nickname ?? ""); setEditingNick(true); }}
                  style={{ fontSize: 11, background: "none", border: "none", color: "rgba(240,240,255,0.3)", cursor: "pointer", padding: 0, lineHeight: 1 }} title="Edit nickname">✏️</button>
              </div>
            )}
            <div className={styles.hudLevel}>{`LV.${kid.level} · ${theme.kidRole.toUpperCase()}`}</div>
          </div>
          <span className={styles.xpLabel}>{xpProg?.current}/{xpProg?.needed} XP</span>
        </div>
        <div className={styles.hudControls}>
          <div className={styles.starBal}>
            {theme.pointEmoji} {kid.pointBalance}
          </div>
          {kid.streakDays > 0 && <div className={styles.streak}>🔥 {kid.streakDays}</div>}
          {/* Notification bell */}
          {(() => {
            const notifs = buildNotifications().filter((n) => !dismissedNotifIds.has(n.id));
            const unread = notifs.filter((n) => !readNotifIds.has(n.id)).length;
            return (
              <button className={styles.bellBtn} onClick={() => setShowNotifs((v) => !v)}>
                🔔
                {unread > 0 && <span className={styles.bellBadge}>{unread > 9 ? "9+" : unread}</span>}
              </button>
            );
          })()}
          <button className={styles.bellBtn} onClick={() => navigate("/kid/theme")} title="Change theme">
            🎨
          </button>
          {isKidPinSession && (
            <button className={styles.bellBtn} onClick={openChangPin} title="Change PIN"
              style={{ fontSize: 16 }}>
              🔐
            </button>
          )}
        </div>
      </div>

      {/* Avatar picker — fixed overlay so overflow:hidden on kidCard2 can't clip it */}
      {pickingAvatar && (
        <>
          <div onClick={() => setPickingAvatar(false)} style={{ position: "fixed", inset: 0, zIndex: 99 }} />
          <div style={{ position: "fixed", top: isPreviewMode() ? 90 : 74, left: 70, zIndex: 100, background: "#1a1a35", border: "1px solid rgba(255,215,0,0.35)", borderRadius: 16, padding: 12, boxShadow: "0 8px 32px rgba(0,0,0,0.7)", minWidth: 216 }}>
            <div style={{ fontSize: 11, fontFamily: "var(--font-display)", fontWeight: 800, color: "rgba(255,215,0,0.7)", marginBottom: 8 }}>Pick your avatar</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 4 }}>
              {(THEME_AVATARS[kid.theme ?? "base"] ?? THEME_AVATARS.base).map((e) => (
                <button key={e} onClick={() => saveAvatar(e)}
                  style={{ fontSize: 24, background: kid.avatar === e ? "rgba(255,215,0,0.2)" : "rgba(255,255,255,0.04)", border: kid.avatar === e ? "1px solid rgba(255,215,0,0.6)" : "1px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: 5, cursor: "pointer" }}>
                  {e}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* PIN change modal */}
      {changingPin && (
        <div onClick={(e) => e.target === e.currentTarget && setChangingPin(false)}
          style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(5,5,15,0.85)", backdropFilter: "blur(16px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div style={{ background: "linear-gradient(160deg,#2a1f5c,#08060f)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 20, padding: "28px 24px", maxWidth: 340, width: "100%", display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 900, fontSize: 18 }}>
              🔐 {pinStep === "new" ? "Set New PIN" : "Confirm New PIN"}
            </div>
            <div style={{ fontSize: 13, color: "rgba(240,240,255,0.5)" }}>
              {pinStep === "new" ? "Enter a new PIN (at least 5 digits)" : "Re-enter your new PIN to confirm"}
            </div>
            <input
              autoFocus
              type="password" inputMode="numeric" maxLength={10}
              placeholder="•••••"
              value={pinStep === "new" ? newPin : confirmPin}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, "");
                pinStep === "new" ? setNewPin(v) : setConfirmPin(v);
              }}
              onKeyDown={(e) => { if (e.key === "Enter") submitPinChange(); if (e.key === "Escape") setChangingPin(false); }}
              style={{ padding: "12px 16px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.15)", background: "rgba(0,0,0,0.4)", color: "#fff", fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 22, textAlign: "center", letterSpacing: "0.2em" }}
            />
            {pinError && <div style={{ color: "#f87171", fontSize: 13, textAlign: "center" }}>{pinError}</div>}
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setChangingPin(false)}
                style={{ flex: 1, padding: "11px 0", borderRadius: 10, background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)", color: "rgba(240,240,255,0.7)", fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
                Cancel
              </button>
              <button onClick={submitPinChange} disabled={pinSaving || (pinStep === "new" ? newPin.length < 5 : confirmPin.length < 5)}
                style={{ flex: 1, padding: "11px 0", borderRadius: 10, background: "linear-gradient(180deg,#ffe257,#ffd700)", color: "#1a1a2e", fontFamily: "var(--font-display)", fontWeight: 900, fontSize: 14, cursor: "pointer", opacity: (pinSaving || (pinStep === "new" ? newPin.length < 5 : confirmPin.length < 5)) ? 0.5 : 1, border: "none" }}>
                {pinSaving ? "Saving…" : pinStep === "new" ? "Next →" : "Save PIN"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification panel */}
      {showNotifs && (() => {
        const allNotifs = buildNotifications().filter((n) => !dismissedNotifIds.has(n.id));
        return (
          <div className={styles.notifPanel} onClick={(e) => e.stopPropagation()}>
            <div className={styles.notifHeader}>
              <span>🔔 Notifications</span>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {allNotifs.length > 0 && (
                  <button type="button" className={styles.notifClearAll}
                    onClick={() => allNotifs.forEach((n) => dismissNotif(n.id))}>
                    Clear all
                  </button>
                )}
                <button type="button" className={styles.notifClose} onClick={() => setShowNotifs(false)}>✕</button>
              </div>
            </div>
            {allNotifs.length === 0 ? (
              <div className={styles.notifEmpty}>All caught up! Nothing new.</div>
            ) : (
              allNotifs.map((n) => {
                const isRead = readNotifIds.has(n.id);
                return (
                  <div key={n.id}
                    className={`${styles.notifRow} ${!isRead ? styles.notifUnread : ""}`}
                    onClick={() => markRead(n.id)}
                    style={{ cursor: "pointer" }}
                  >
                    {!isRead && <div className={styles.notifDot} />}
                    <div className={styles.notifEmoji}>{n.emoji}</div>
                    <div className={styles.notifBody}>
                      <div className={styles.notifText}>{n.text}</div>
                      <div className={styles.notifDetail}>{n.detail}</div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
                      <div className={styles.notifTime}>
                        {new Date(n.at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      </div>
                      <button type="button" className={styles.notifDeleteBtn}
                        onClick={(e) => { e.stopPropagation(); dismissNotif(n.id); }}>✕</button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        );
      })()}

      {/* Body */}
      <div className={`${styles.body} ${!isHouseOpen ? styles.bodyCollapsed : ""}`}>
        <div className={`${styles.left} ${!isHouseOpen ? styles.leftCollapsed : ""}`}>
          <div className={styles.mapCard}>
            {/* Accordion header */}
            <button className={styles.mapHeaderBtn} onClick={toggleHouse}>
              <div>
                <h2 className={styles.mapTitle}>{theme.homeTitle}</h2>
                <div className={styles.mapRemain}>
                  {activeChallenges.length > 0
                    ? `${activeChallenges.length} ${activeChallenges.length !== 1 ? theme.challengeNamePlural : theme.challengeName} hiding · tap a room`
                    : isHouseOpen ? `No ${theme.challengeNamePlural.toLowerCase()} right now` : `Tap to explore ${theme.homeTitle.toLowerCase()}`}
                </div>
              </div>
              <span className={`${styles.mapChevron} ${isHouseOpen ? styles.mapChevronOpen : ""}`}>›</span>
            </button>

            {/* Accordion body */}
            {isHouseOpen && (
              activeChallenges.length > 0 ? (
                <div className={styles.rooms}>
                  {challengesByRoom.map((r) => (
                    <button key={r.roomId} className={styles.room} onClick={() => setSelectedRoom(r)}>
                      <div className={styles.roomTop}>
                        <span className={styles.roomEmoji}>{r.emoji}</span>
                        <span className={styles.roomName}>{r.name}</span>
                      </div>
                      {r.roomChallenges.length > 1 && <div className={styles.roomBadge}>{r.roomChallenges.length}</div>}
                      <div className={styles.roomDots}>
                        {r.roomChallenges.map((c) => (
                          <div key={c.challengeId} className={`${styles.dot} ${styles[c.difficulty]}`}>★</div>
                        ))}
                        {r.roomChallenges.length === 0 && <div className={styles.dotEmpty}>—</div>}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className={styles.noQuestState}>
                  <div className={styles.noQuestIcon}>{theme.emoji}</div>
                  <div className={styles.noQuestTitle}>{theme.homeTitle} is quiet</div>
                  <div className={styles.noQuestDesc}>
                    No {theme.challengeNamePlural.toLowerCase()} are hiding right now. Ask your {theme.parentRole} to add some!
                  </div>
                  {questRequested ? (
                    <div className={styles.questRequestedMsg}>
                      ✅ Request sent to your {theme.parentRole}!
                    </div>
                  ) : (
                    <button className={styles.askQuestBtn} onClick={handleAskForQuest}>
                      ✨ Ask for a {theme.challengeName}
                    </button>
                  )}
                </div>
              )
            )}
          </div>
        </div>

        <div className={styles.right} style={{ overflowY: "auto" }}>
          {/* Progress ring */}
          {(() => {
            const goal = kid.weeklyPointGoal ?? 0;
            const earned = starsThisWeek();
            const pct = goal > 0 ? Math.min(earned / goal, 1) : 0;
            const C = 2 * Math.PI * 32;
            const today = todayStr();
            const todayTasks = taskAssignments.filter((a) => a.dueDate.slice(0, 10) === today && a.status !== "rejected");
            const todayDone  = todayTasks.filter((a) => a.status === "approved").length;
            const monday = mondayStr();
            const weekTasks  = taskAssignments.filter((a) => a.status !== "rejected" && a.dueDate.slice(0, 10) >= monday);
            const weekDone   = weekTasks.filter((a) => a.status === "approved").length;

            return (
              <div className={styles.progressCard}>
                <div className={styles.ringWrap}>
                  <svg viewBox="0 0 76 76" width="76" height="76">
                    <circle cx="38" cy="38" r="32" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="8" />
                    <circle cx="38" cy="38" r="32" fill="none" stroke="var(--theme-accent)" strokeWidth="8"
                      strokeDasharray={C} strokeDashoffset={C * (1 - pct)}
                      strokeLinecap="round" style={{ transition: "stroke-dashoffset 0.6s ease-out" }} />
                  </svg>
                  <div className={styles.ringCenter}>
                    <div className={styles.ringStars}>{earned}</div>
                    <div className={styles.ringLabel}>{goal > 0 ? `/ ${goal} ${theme.pointEmoji}` : theme.pointEmoji}</div>
                  </div>
                </div>
                <div className={styles.ringStats}>
                  <div className={styles.ringGoalTxt}>{theme.pointEmoji} {goal > 0 ? "Weekly Goal" : `${theme.pointNamePlural} This Week`}</div>
                  <div className={styles.statRow}>
                    <div className={styles.statBadge}>
                      <div className={`${styles.statVal} ${styles.green}`}>{todayDone}/{todayTasks.length}</div>
                      <div className={styles.statLbl}>☀️ Today</div>
                    </div>
                    <div className={styles.statBadge}>
                      <div className={`${styles.statVal} ${styles.purple}`}>{weekDone}/{weekTasks.length}</div>
                      <div className={styles.statLbl}>📅 This Week</div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Tasks with day navigation */}
          {(() => {
            const PAGE_SIZE = 5;
            const selDate = selectedDateStr();
            const isFuture = dayOffset > 0;
            const dayTasks = taskAssignments
              .filter((a) => taskMatchesDate(a, selDate))
              .sort((a, b) => {
                const order = { assigned: 0, submitted: 1, approved: 2 };
                return (order[a.status as keyof typeof order] ?? 3) - (order[b.status as keyof typeof order] ?? 3);
              });

            const totalPages = Math.ceil(dayTasks.length / PAGE_SIZE);
            const pagedTasks = dayTasks.slice(taskPage * PAGE_SIZE, (taskPage + 1) * PAGE_SIZE);
            const done = dayTasks.filter((a) => a.status === "approved").length;

            return (
              <div className={styles.tasksSect}>
                {/* Day navigation header */}
                <div className={styles.tasksDayNav}>
                  <button className={styles.tasksDayBtn} onClick={() => changeDayOffset(-1)}>‹</button>
                  <div className={styles.tasksDayCenter}>
                    <div className={styles.tasksSectTitle}>📋 {dayLabel().toUpperCase()}</div>
                    {dayTasks.length > 0 && (
                      <div className={styles.tasksSectCount}>{done} of {dayTasks.length} done</div>
                    )}
                  </div>
                  <button className={styles.tasksDayBtn} onClick={() => changeDayOffset(+1)}>›</button>
                </div>

                {isFuture && dayTasks.length > 0 && (
                  <div className={styles.tasksFutureNote}>
                    🔒 These tasks unlock on {dayLabel()} — you can see them but not complete them yet
                  </div>
                )}

                {dayTasks.length === 0 ? (
                  <div className={styles.tasksEmpty}>
                    {isFuture ? "No tasks scheduled for this day yet" : "No tasks for this day"}
                  </div>
                ) : (
                  <>
                    {pagedTasks.map((a) => {
                      const isDone      = a.status === "approved";
                      const isPending   = a.status === "submitted";
                      const wasSentBack = !isDone && !isPending && !!a.rejectionReason;
                      const hint        = tierHint(a.dueDate);
                      return (
                        <div key={a.assignmentId} className={`${styles.taskRow} ${isDone ? styles.taskRowDone : ""}`}>
                          <div className={styles.taskEmoji}>{a.taskEmoji}</div>
                          <div className={styles.taskBody}>
                            <div className={`${styles.taskName} ${isDone ? styles.taskNameDone : ""}`}>{a.taskName}</div>
                            <div className={styles.taskMeta}>
                              {isDone       && `Done · +${a.pointsAwarded} ${theme.pointEmoji}`}
                              {isPending    && "Waiting for parent approval…"}
                              {wasSentBack  && <span className={styles.taskHintLate}>⚠️ Try again: {a.rejectionReason}</span>}
                              {!isDone && !isPending && !wasSentBack && isFuture && (
                                <span style={{ color: "rgba(240,240,255,0.35)" }}>Scheduled</span>
                              )}
                              {!isDone && !isPending && !wasSentBack && !isFuture && (
                                <span className={hint.cls}>{hint.text}</span>
                              )}
                            </div>
                          </div>
                          {!isDone && !isPending && !isFuture && (
                            <button type="button" className={styles.taskDoneBtn}
                              disabled={submittingTask === a.assignmentId}
                              onClick={() => submitTask(a.assignmentId)}>
                              {submittingTask === a.assignmentId ? "…" : "Done!"}
                            </button>
                          )}
                          {!isDone && !isPending && isFuture && (
                            <div className={styles.taskFutureLock}>🔒</div>
                          )}
                          {isPending && <div className={styles.taskPending}>⏳ Pending</div>}
                          {isDone    && <span style={{ fontSize: 18 }}>✅</span>}
                        </div>
                      );
                    })}

                    {totalPages > 1 && (
                      <div className={styles.tasksPagination}>
                        <button className={styles.tasksPageBtn} disabled={taskPage === 0}
                          onClick={() => setTaskPage((p) => p - 1)}>‹ Prev</button>
                        <span className={styles.tasksPageInfo}>{taskPage + 1} / {totalPages}</span>
                        <button className={styles.tasksPageBtn} disabled={taskPage >= totalPages - 1}
                          onClick={() => setTaskPage((p) => p + 1)}>Next ›</button>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })()}

          {/* Active quest card */}
          <div className={styles.questCard}>
            <div className={styles.questLabel}>Active {theme.challengeName}</div>
            <div className={styles.questTitle}>Tap a room to start a {theme.challengeName.toLowerCase()}</div>
            <div className={styles.questDesc}>Find {theme.challengeNamePlural.toLowerCase()} hidden around {theme.homeTitle.toLowerCase()} and complete them to earn rewards!</div>
          </div>
        </div>
      </div>

      {/* Bottom nav */}
      <nav className={styles.nav}>
        <div className={`${styles.navBtn} ${styles.navActive}`}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 11 L12 3 L21 11"/><path d="M5 10 V21 H19 V10"/></svg>
          Home
        </div>
        <div className={styles.navBtn} onClick={() => navigate("/kid/quest-log")}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 9 H21"/></svg>
          {theme.challengeLogTitle}
        </div>
        <div className={styles.navBtn} onClick={() => navigate("/kid/cashout")}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 21 L21 21"/><path d="M3 10 L12 3 L21 10"/></svg>
          {theme.pointBankTitle}
        </div>
        <div className={styles.navBtn} onClick={() => navigate("/kid/store")}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 7 L21 7 L20 20 L4 20 Z"/><path d="M9 7 V5 A3 3 0 0 1 15 5 V7"/></svg>
          Store
        </div>
        <div className={`${styles.navBtn} ${styles.navSignOut}`} onClick={() => { clearAuth(); selectKid(null); navigate("/"); }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          Sign Out
        </div>
      </nav>

      {/* Room overlay */}
      {selectedRoom && (
        <div className={styles.overlay} onClick={(e) => e.target === e.currentTarget && setSelectedRoom(null)}>
          <div className={styles.roomDetail}>
            <button className={styles.overlayClose} onClick={() => setSelectedRoom(null)}>✕</button>
            <div className={styles.roomDetailHead}>
              <span className={styles.roomDetailEmoji}>{selectedRoom.emoji}</span>
              <div>
                <h2 className={styles.roomDetailTitle}>{selectedRoom.name}</h2>
                <div className={styles.roomDetailSub}>
                  {(() => {
                    const count = (selectedRoom as Room & { roomChallenges: Challenge[] }).roomChallenges?.length ?? 0;
                    return `${count} challenge${count !== 1 ? "s" : ""} hiding here`;
                  })()}
                </div>
              </div>
            </div>
            <div className={styles.starList}>
              {(challengesByRoom.find((r) => r.roomId === selectedRoom.roomId)?.roomChallenges ?? []).map((c) => (
                <button key={c.challengeId} className={`${styles.starItem} ${styles[c.difficulty] ?? ""}`}
                  onClick={() => { setSelectedRoom(null); setCatchStar(c); }}>
                  <div className={`${styles.starIcon} ${styles[c.difficulty]}`}>{c.emoji}</div>
                  <div className={styles.starInfo}>
                    <div className={styles.starName}>{c.challengeName}</div>
                    <div className={styles.starDesc}>{c.difficulty} · {c.challengeDescription}</div>
                  </div>
                  <div className={styles.starReward}>{c.pointReward} {theme.pointEmoji}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Catch screen overlay */}
      {catchStar && (
        <div className={styles.overlay}>
          <div className={styles.catchScreen}>
            <div className={styles.catchTop}>
              <button className={styles.overlayClose} onClick={() => setCatchStar(null)}>✕</button>
              <div className={`${styles.rarityChip} ${styles[catchStar.difficulty]}`}>◆ {theme.difficulty[catchStar.difficulty as keyof typeof theme.difficulty]}</div>
              <div style={{ width: 44 }} />
            </div>

            <div className={styles.catchMain}>
              <div className={styles.creatureBox}>
                <div className={styles.haloO} />
                <div className={styles.haloI} />
                <div className={styles.creature}>{catchStar.emoji}</div>
              </div>
              <div className={styles.catchInfo}>
                <div className={styles.catchTitle}>{catchStar.challengeName}</div>
                <div className={styles.catchSub}>Complete the challenge below, then tap CAPTURE to submit for approval.</div>
                <div className={styles.questBox}>
                  <div className={styles.questLabel2}>The Challenge</div>
                  <div className={styles.questName}>{catchStar.challengeDescription}</div>
                  <div className={styles.qstats}>
                    <div className={styles.qst}><div className={styles.qstL}>Reward</div><div className={styles.qstV}>{catchStar.pointReward} {theme.pointEmoji}</div></div>
                    <div className={styles.qst}><div className={styles.qstL}>XP</div><div className={styles.qstV}>+{catchStar.xp}</div></div>
                  </div>
                </div>
              </div>
            </div>

            <input
              ref={photoRef}
              type="file" accept="image/*" capture="environment"
              style={{ display:"none" }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) submitQuest(catchStar, f); }}
            />

            <div className={styles.catchCta}>
              <button className={styles.ctaGhost} onClick={() => setCatchStar(null)}>Not yet</button>
              <button className={styles.ctaGo} disabled={submitting} onClick={() => photoRef.current?.click()}>
                {uploading ? "Uploading…" : submitting ? "Submitting…" : "CAPTURE! 🌟"}
              </button>
            </div>
          </div>
        </div>
      )}

      {submitted && (
        <div className={styles.overlay}>
          <div className={styles.submitSuccess}>
            <div className={styles.submitIcon}>✅</div>
            <div className={styles.submitTitle}>Quest submitted!</div>
            <div className={styles.submitSub}>
              Your {theme.parentRole} will review your photo and award your {theme.pointNamePlural.toLowerCase()}.
            </div>
            <div className={styles.submitReward}>
              <span>{theme.pointEmoji} +{submitted.pointReward} {theme.pointNamePlural.toLowerCase()}</span>
              <span className={styles.submitDot}>·</span>
              <span>✨ +{submitted.xp} XP</span>
            </div>
            <button type="button" className={styles.ctaGhost} onClick={() => setSubmitted(null)}>
              Back to exploring
            </button>
          </div>
        </div>
      )}
    </div>
    </ThemeWrapper>
  );
}
